import React, { useState } from "react";
import { Sparkles, Check, AlertTriangle, X as XIcon } from "lucide-react";

/**
 * FeedbackBox — satisfaction feedback after each analysis step.
 * Heetch Pro dark theme.
 */
export function FeedbackBox({ stepName, onSubmit, onSkip }) {
  const [satisfaction, setSatisfaction] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = satisfaction === "yes" || (satisfaction && comment.trim().length >= 3);

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    await onSubmit({ satisfaction, comment: comment.trim() });
  };

  return (
    <div
      className="rounded-xl overflow-hidden border border-emerald-500/40"
      style={{ background: "linear-gradient(135deg, rgba(0,220,130,0.15), rgba(0,220,130,0.04))" }}
      data-testid={`feedback-box-${stepName}`}
    >
      <div className="px-5 py-4 border-b border-emerald-500/30 bg-[var(--surface-alt)]/50">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-500 grid place-items-center border border-emerald-500">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={1.8} />
          </div>
          <div>
            <p className="hp-label" style={{ color: "#86efac" }}>
              Feedback Prosperr
            </p>
            <p className="text-sm font-semibold text-[var(--text)]">
              Cette analyse correspond à vos attentes ?
            </p>
          </div>
        </div>
      </div>

      <div className="px-5 py-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          <button
            onClick={() => setSatisfaction("yes")}
            data-testid="feedback-yes"
            className={`rounded-lg border px-4 py-3.5 text-sm font-semibold transition-colors active:scale-[0.98] flex items-center gap-2 justify-center ${
              satisfaction === "yes"
                ? "bg-emerald-600 text-white border-emerald-500"
                : "bg-[var(--surface)] text-zinc-300 border-[var(--border)] hover:border-emerald-500/50"
            }`}
          >
            <Check className="w-4 h-4" strokeWidth={2.5} /> Parfaitement
          </button>
          <button
            onClick={() => setSatisfaction("partial")}
            data-testid="feedback-partial"
            className={`rounded-lg border px-4 py-3.5 text-sm font-semibold transition-colors active:scale-[0.98] flex items-center gap-2 justify-center ${
              satisfaction === "partial"
                ? "bg-amber-600 text-white border-amber-500"
                : "bg-[var(--surface)] text-zinc-300 border-[var(--border)] hover:border-amber-500/50"
            }`}
          >
            <AlertTriangle className="w-4 h-4" /> Partiellement
          </button>
          <button
            onClick={() => setSatisfaction("no")}
            data-testid="feedback-no"
            className={`rounded-lg border px-4 py-3.5 text-sm font-semibold transition-colors active:scale-[0.98] flex items-center gap-2 justify-center ${
              satisfaction === "no"
                ? "bg-rose-600 text-white border-rose-500"
                : "bg-[var(--surface)] text-zinc-300 border-[var(--border)] hover:border-rose-500/50"
            }`}
          >
            <XIcon className="w-4 h-4" strokeWidth={2.5} /> Non
          </button>
        </div>

        {(satisfaction === "partial" || satisfaction === "no") && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-emerald-300">
              Qu'est-ce qui manquerait ou vous aiderait le plus ?
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={400}
              placeholder="Votre retour nous aide à améliorer Prosperr en continu…"
              data-testid="feedback-comment"
              className="hp-input w-full resize-none"
            />
            <p className="text-[11px] text-zinc-500 text-right tabular-nums">{comment.length}/400</p>
          </div>
        )}

        {satisfaction === "yes" && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-emerald-300">
              Un détail à ajouter ? (optionnel)
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              maxLength={400}
              placeholder="Un point à affiner ou une suggestion…"
              data-testid="feedback-comment"
              className="hp-input w-full resize-none"
            />
          </div>
        )}

        <div className="flex gap-2.5 pt-2">
          <button
            onClick={onSkip}
            data-testid="feedback-skip"
            className="hp-btn hp-btn-secondary flex-1 h-11"
          >
            Passer
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            data-testid="feedback-submit"
            className="hp-btn hp-btn-primary flex-1 h-11"
          >
            {submitting ? "Envoi…" : "Envoyer et continuer"}
          </button>
        </div>
      </div>
    </div>
  );
}
